import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar, Nav, NavDropdown, Container, Button, Offcanvas, Badge } from 'react-bootstrap';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import logoWhiteHeader from '../logo_white_header_clear.png';

const Header = () => {
    const { isAuthenticated, user, logout, hasRole } = useAuth();
    const navigate = useNavigate();
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const { items } = useCart();
    const { unreadCount: notificationCount } = useNotifications();
    const { settings } = useStoreSettings();
    const cartCount = items.reduce((sum, i) => sum + i.quantity, 0);

    const myAccountTitle = (
        <span>
            My Account{' '}
            {notificationCount > 0 && (
                <Badge bg="danger" pill>
                    {notificationCount}
                </Badge>
            )}
        </span>
    );

    const getDashboardLink = () => {
        if (hasRole(ROLES.BUYER)) return '/';
        if (hasRole(ROLES.CASHIER)) return '/pos';
        if (hasRole(ROLES.INVENTORY_MANAGER)) return '/inventory';
        if (hasRole(ROLES.ADMINISTRATOR)) return '/admin';
        if (hasRole(ROLES.SUPER_USER)) return '/superuser';
        return '/';
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <Navbar bg="dark" variant="dark" expand="lg" sticky="top">
            <Container>
                <Button
                    variant="outline-light"
                    className=" border-0 px-2 py-1 d-lg-none"
                    onClick={() => setShowOffcanvas(true)}
                    aria-label="Open menu"
                >
                    &#9776;
                </Button>
                <Link to="/" className="d-flex align-items-center ms-auto ms-lg-0 me-1" aria-label="Home">
                    <img
                        src={logoWhiteHeader}
                        alt="Store logo"
                        width="44px"
                        height="44px"
                        style={{ display: 'block' }}
                    />
                </Link>
                <Navbar.Brand as={Link} to="/" className="ms-0 me-0">{settings.store_name}</Navbar.Brand>
                <Navbar.Collapse id="basic-navbar-nav">
                    <Nav className="me-auto">
                        <Nav.Link as={Link} to="/">Home</Nav.Link>
                        {hasRole(ROLES.BUYER) && (
                            <NavDropdown title={myAccountTitle} id="buyer-account-nav-dropdown">
                                <NavDropdown.Item as={Link} to="/buyer/profile">Profile</NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/buyer/addresses">Addresses</NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/buyer/orders">Orders</NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/buyer/notifications">
                                    Notifications
                                    {notificationCount > 0 && (
                                        <Badge bg="danger" pill className="ms-2">
                                            {notificationCount}
                                        </Badge>
                                    )}
                                </NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/buyer/invite-friends">Invite Friends</NavDropdown.Item>
                            </NavDropdown>
                        )}
                    </Nav>
                    <Nav>
                        {isAuthenticated() ? (
                            <>
                                <Navbar.Text className="me-3">
                                    Hello, {user?.name || user?.email || 'User'}!
                                </Navbar.Text>
                                {hasRole(ROLES.BUYER) && (
                                    <Button
                                        variant="outline-light"
                                        size="sm"
                                        className="me-2 position-relative"
                                        onClick={() => navigate('/buyer/cart')}
                                    >
                                        🛒 Cart
                                        {cartCount > 0 && (
                                            <Badge bg="danger" pill className="position-absolute top-0 start-100 translate-middle">
                                                {cartCount}
                                            </Badge>
                                        )}
                                    </Button>
                                )}
                                {(hasRole(ROLES.CASHIER) || hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                    <Button
                                        variant="outline-light"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => navigate('/pos')}
                                    >
                                        Sales Desk
                                    </Button>
                                )}
                                {(hasRole(ROLES.INVENTORY_MANAGER) || hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER) || hasRole(ROLES.CASHIER)) && (
                                    <Button
                                        variant="outline-light"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => navigate(getDashboardLink())}
                                    >
                                        Dashboard
                                    </Button>
                                )}
                                <Button variant="outline-danger" size="sm" onClick={handleLogout}>
                                    Logout
                                </Button>
                                {(hasRole(ROLES.INVENTORY_MANAGER) || hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                    <NavDropdown title="Admin Tools" id="admin-nav-dropdown" align="end">
                                        {hasRole(ROLES.INVENTORY_MANAGER) && (
                                            <>
                                                <NavDropdown.Item as={Link} to="/products">
                                                    Manage Products
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/shipments">
                                                    Manage Shipments
                                                </NavDropdown.Item>
                                            </>
                                        )}
                                        {(hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                            <>
                                                <NavDropdown.Item as={Link} to="/categories">
                                                    Manage Categories
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/attributes">
                                                    Manage Attributes
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/gst-rates">
                                                    Manage GST Rates
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/costing-parameters">
                                                    Manage Costing Parameters
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/india-states">
                                                    Manage States / UTs
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/suppliers">
                                                    Manage Suppliers
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/shipments">
                                                    Manage Shipments
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/products">
                                                    Manage Products
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/orders">
                                                    View Orders
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/refund-requests">
                                                    Refund Requests
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/reviews-admin">
                                                    Manage Reviews
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/contact-messages">
                                                    Contact Messages
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/admin/notifications">
                                                    Notification Manager
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/admin/invitees">
                                                    Manage Invitees
                                                </NavDropdown.Item>
                                                <NavDropdown.Item as={Link} to="/communication-templates">
                                                    Communication Templates
                                                </NavDropdown.Item>
                                                {hasRole(ROLES.SUPER_USER) && (
                                                    <NavDropdown.Item as={Link} to="/superuser/users">
                                                        Manage Users
                                                    </NavDropdown.Item>
                                                )}
                                                {hasRole(ROLES.SUPER_USER) && (
                                                    <NavDropdown.Item as={Link} to="/superuser/promo-codes">
                                                        Manage Promo Codes
                                                    </NavDropdown.Item>
                                                )}
                                                {(hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                                    <NavDropdown.Item as={Link} to="/store-settings">
                                                        Store Settings
                                                    </NavDropdown.Item>
                                                )}
                                                {(hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                                    <NavDropdown.Item as={Link} to="/gmc-sync">
                                                        Google Merchant Center Sync
                                                    </NavDropdown.Item>
                                                )}
                                            </>
                                        )}
                                    </NavDropdown>
                                )}
                            </>
                        ) : (
                            <>
                                <Nav.Link as={Link} to="/login">Login</Nav.Link>
                                <Nav.Link as={Link} to="/register">Register</Nav.Link>
                            </>
                        )}
                    </Nav>
                </Navbar.Collapse>

                {/* Mobile Offcanvas Menu */}
                <Offcanvas show={showOffcanvas} onHide={() => setShowOffcanvas(false)} placement="start">
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>Menu</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <Nav className="flex-column">
                            <Nav.Link as={Link} to="/" onClick={() => setShowOffcanvas(false)}>Home</Nav.Link>
                            {hasRole(ROLES.BUYER) && (
                                <NavDropdown title={myAccountTitle} id="buyer-account-offcanvas-dropdown">
                                    <NavDropdown.Item as={Link} to="/buyer/profile" onClick={() => setShowOffcanvas(false)}>Profile</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/buyer/addresses" onClick={() => setShowOffcanvas(false)}>Addresses</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/buyer/orders" onClick={() => setShowOffcanvas(false)}>Orders</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/buyer/notifications" onClick={() => setShowOffcanvas(false)}>
                                        Notifications
                                        {notificationCount > 0 && (
                                            <Badge bg="danger" pill className="ms-2">
                                                {notificationCount}
                                            </Badge>
                                        )}
                                    </NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/buyer/invite-friends" onClick={() => setShowOffcanvas(false)}>Invite Friends</NavDropdown.Item>
                                </NavDropdown>
                            )}
                            {isAuthenticated() ? (
                                <>
                                    <div className="mb-3">
                                        <strong>Hello, {user?.name || user?.email || 'User'}!</strong>
                                    </div>
                                    {hasRole(ROLES.BUYER) && (
                                        <Button
                                            variant="primary"
                                            className="w-100 mb-2 position-relative"
                                            onClick={() => {
                                                navigate('/buyer/cart');
                                                setShowOffcanvas(false);
                                            }}
                                        >
                                            🛒 Cart
                                            {cartCount > 0 && (
                                                <Badge bg="danger" pill className="position-absolute top-0 start-100 translate-middle">
                                                    {cartCount}
                                                </Badge>
                                            )}
                                        </Button>
                                    )}
                                    {(hasRole(ROLES.INVENTORY_MANAGER) || hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                        <Button
                                            variant="primary"
                                            className="w-100 mb-2"
                                            onClick={() => {
                                                navigate(getDashboardLink());
                                                setShowOffcanvas(false);
                                            }}
                                        >
                                            Dashboard
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline-danger"
                                        className="w-100 mb-3"
                                        onClick={() => {
                                            handleLogout();
                                            setShowOffcanvas(false);
                                        }}
                                    >
                                        Logout
                                    </Button>
                                    {(hasRole(ROLES.INVENTORY_MANAGER) || hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                        <div>
                                            <h6>Admin Tools</h6>
                                            {hasRole(ROLES.INVENTORY_MANAGER) && (
                                                <>
                                                    <Nav.Link as={Link} to="/products" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Products
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/shipments" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Shipments
                                                    </Nav.Link>
                                                </>
                                            )}
                                            {(hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                                <>
                                                    <Nav.Link as={Link} to="/categories" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Categories
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/attributes" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Attributes
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/gst-rates" onClick={() => setShowOffcanvas(false)}>
                                                        Manage GST Rates
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/costing-parameters" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Costing Parameters
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/india-states" onClick={() => setShowOffcanvas(false)}>
                                                        Manage States / UTs
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/suppliers" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Suppliers
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/shipments" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Shipments
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/products" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Products
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/orders" onClick={() => setShowOffcanvas(false)}>
                                                        View Orders
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/refund-requests" onClick={() => setShowOffcanvas(false)}>
                                                        Refund Requests
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/reviews-admin" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Reviews
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/contact-messages" onClick={() => setShowOffcanvas(false)}>
                                                        Contact Messages
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/admin/notifications" onClick={() => setShowOffcanvas(false)}>
                                                        Notification Manager
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/admin/invitees" onClick={() => setShowOffcanvas(false)}>
                                                        Manage Invitees
                                                    </Nav.Link>
                                                    <Nav.Link as={Link} to="/communication-templates" onClick={() => setShowOffcanvas(false)}>
                                                        Communication Templates
                                                    </Nav.Link>
                                                    {hasRole(ROLES.SUPER_USER) && (
                                                        <Nav.Link as={Link} to="/superuser/users" onClick={() => setShowOffcanvas(false)}>
                                                            Manage Users
                                                        </Nav.Link>
                                                    )}
                                                    {hasRole(ROLES.SUPER_USER) && (
                                                        <Nav.Link as={Link} to="/superuser/promo-codes" onClick={() => setShowOffcanvas(false)}>
                                                            Manage Promo Codes
                                                        </Nav.Link>
                                                    )}
                                                    {(hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                                        <Nav.Link as={Link} to="/store-settings" onClick={() => setShowOffcanvas(false)}>
                                                            Store Settings
                                                        </Nav.Link>
                                                    )}
                                                    {(hasRole(ROLES.ADMINISTRATOR) || hasRole(ROLES.SUPER_USER)) && (
                                                        <Nav.Link as={Link} to="/gmc-sync" onClick={() => setShowOffcanvas(false)}>
                                                            Google Merchant Center Sync
                                                        </Nav.Link>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Nav.Link as={Link} to="/login" onClick={() => setShowOffcanvas(false)}>Login</Nav.Link>
                                    <Nav.Link as={Link} to="/register" onClick={() => setShowOffcanvas(false)}>Register</Nav.Link>
                                </>
                            )}
                        </Nav>
                    </Offcanvas.Body>
                </Offcanvas>
            </Container>
        </Navbar>
    );
};

export default Header;

